/**
 * BudgetAlertBanner Component
 *
 * Displays budget variance alerts for projects
 * Shows yellow warning at 80%+ and red critical alert at 100%+
 *
 * @module components/common/BudgetAlertBanner
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { BUDGET_THRESHOLD } from '../../utils/constants';

/**
 * BudgetAlertBanner component
 * @param {Object} props
 * @param {number} props.variancePercentage - Budget usage percentage
 * @param {number} props.quotedAmount - Original quoted amount
 * @param {number} props.spentAmount - Amount spent so far
 * @param {string} props.className - Additional CSS classes
 */
const BudgetAlertBanner = ({
  variancePercentage,
  quotedAmount,
  spentAmount,
  className = '',
}) => {
  // Don't show if no alert needed
  if (variancePercentage < BUDGET_THRESHOLD.WARNING) {
    return null;
  }

  const isCritical = variancePercentage >= BUDGET_THRESHOLD.CRITICAL;
  const variant = isCritical ? 'danger' : 'warning';
  const icon = isCritical ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill';
  const title = isCritical ? 'CRÍTICO: Presupuesto Excedido' : 'ALERTA: Presupuesto Próximo al Límite';

  const remainingAmount = quotedAmount - spentAmount;
  const isOverBudget = remainingAmount < 0;

  return (
    <Alert variant={variant} className={`d-flex align-items-start ${className}`}>
      <i className={`bi ${icon} me-3`} style={{ fontSize: '1.5rem' }}></i>
      <div className="flex-grow-1">
        <Alert.Heading className="h5 mb-2">
          {title}
        </Alert.Heading>
        <div className="mb-2">
          <strong>Uso del presupuesto: {formatPercentage(variancePercentage, 1)}</strong>
        </div>
        <div className="row g-2 small">
          <div className="col-md-4">
            <div className="text-muted">Cotizado:</div>
            <div className="fw-bold">{formatCurrency(quotedAmount)}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted">Gastado:</div>
            <div className="fw-bold">{formatCurrency(spentAmount)}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted">
              {isOverBudget ? 'Excedido:' : 'Disponible:'}
            </div>
            <div className={`fw-bold ${isOverBudget ? 'text-danger' : ''}`}>
              {formatCurrency(Math.abs(remainingAmount))}
            </div>
          </div>
        </div>
        {isCritical && (
          <div className="mt-3 pt-2 border-top">
            <small>
              <i className="bi bi-info-circle me-1"></i>
              Se recomienda revisar los gastos y contactar con el administrador del proyecto.
            </small>
          </div>
        )}
      </div>
    </Alert>
  );
};

BudgetAlertBanner.propTypes = {
  variancePercentage: PropTypes.number.isRequired,
  quotedAmount: PropTypes.number.isRequired,
  spentAmount: PropTypes.number.isRequired,
  className: PropTypes.string,
};

export default BudgetAlertBanner;
